<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { api, ApiError, arr, type Me } from './lib/api'
import { authConfigured, devBypass, signIn, signOut, signedIn } from './lib/auth'
import Spinner from './components/Spinner.vue'
import Badge from './components/Badge.vue'
import NotificationBell from './components/NotificationBell.vue'
import { standards, standardByKey, defaultStandardKey, type StandardEntry } from './lib/standards'

const route = useRoute()
const me = ref<Me | null>(null)
const loading = ref(true)
const authError = ref<string | null>(null)
const tokenProblem = ref<{ code: string; hint: string } | null>(null)

/**
 * Sidebar width. Collapsed to icons rather than hidden entirely: navigation you cannot see is
 * navigation you cannot use, and the point of the request was screen width on wide registers,
 * not the absence of a sidebar. Persisted, because a layout preference that resets every visit
 * is not a preference.
 */
const collapsed = ref(localStorage.getItem('qms.sidebar') === 'collapsed')
function toggleSidebar() {
  collapsed.value = !collapsed.value
  localStorage.setItem('qms.sidebar', collapsed.value ? 'collapsed' : 'open')
}

/** Dark mode toggles the two attributes ABAX's dark theme is scoped to. */
const dark = ref(false)
function applyTheme() {
  const el = document.documentElement
  el.setAttribute('data-theme', 'abax')
  el.setAttribute('data-mode', dark.value ? 'dark' : 'light')
  localStorage.setItem('qms.mode', dark.value ? 'dark' : 'light')
}
function toggleTheme() {
  dark.value = !dark.value
  applyTheme()
}

/**
 * Navigation, grouped by what somebody came to do rather than by what kind of object a page holds.
 *
 * Seventeen flat entries had become unreadable, and the ordering was hiding the pages that matter
 * most: Attestations, Checks and Proposals all mean "somebody is waiting on you", and they sat at
 * positions 12, 14 and 11 with unrelated registers between them. Grouping them together is the
 * point of this structure; the labels are secondary.
 *
 * Three renames come with it, all of them fixing a label that misdescribed its page. "Review" is
 * the connector gate and is now Discoveries; "Review queue" is agent-drafted changes and is now
 * Proposals — near-identical names for unrelated pages meant nobody could tell which was which.
 * And "Registers" held no registers at all: it edits the asset taxonomy, legal entities, org units
 * and locations, so sitting next to Assets and Risks it read as their parent. It is Reference data.
 */
/** The roles that may read AI classification records. Presentation only: qms-api decides. */
const aiClassificationRoles = [
  'ai_triage', 'ai_tool_approval', 'ciso', 'dpo', 'qms_manager', 'qms_admin', 'top_management',
  'internal_auditor', 'external_auditor',
]

const nav: { header: string; items: { to: string; label: string; short: string; adminOnly?: boolean; needsAny?: string[] }[] }[] = [
  {
    // Two summary pages, kept adjacent so the relationship is visible. Readiness is the fuller
    // one — it reads every register plus the management review and the processing record, and says
    // when a section could not be read. Overview predates it and covers assets, risks and the SoA.
    header: 'Where we stand',
    items: [
      { to: '/readiness', label: 'Readiness', short: 'RD' },
      { to: '/dashboard', label: 'Overview', short: 'OV' },
    ],
  },
  {
    // The inventory somebody maintains. Key cards is here rather than under oversight because the
    // credentials are physical assets and the page is a lending register — but it is one line to
    // move if it reads better elsewhere.
    header: 'What we have',
    items: [
      { to: '/assets', label: 'Assets', short: 'AS' },
      { to: '/risks', label: 'Risks', short: 'RI' },
      { to: '/suppliers', label: 'Suppliers', short: 'SU' },
      { to: '/ai-tools', label: 'AI tooling', short: 'AI' },
      // For the governance roles and auditors only: risk tier and role determinations were
      // deliberately kept off the public tooling list. The API refuses everyone else regardless.
      { to: '/ai-classifications', label: 'AI classification', short: 'CL', needsAny: aiClassificationRoles },
      { to: '/documents', label: 'Documents', short: 'DO' },
      { to: '/access', label: 'Key cards', short: 'KC' },
    ],
  },
  {
    // What ABAX asserts to an auditor, as distinct from what it holds.
    header: 'What we claim',
    items: [
      { to: '/soa', label: 'Statement of Applicability', short: 'SoA' },
      { to: '/ropa', label: 'Processing (GDPR)', short: 'PA' },
    ],
  },
  {
    // The three pages that generate work for the person reading. The notification bell points
    // here, so these three being adjacent is what stops the bell and the menu disagreeing.
    header: 'Waiting on you',
    items: [
      { to: '/attest', label: 'Attestations', short: 'AT' },
      { to: '/checks', label: 'Checks', short: 'CK' },
      { to: '/proposals', label: 'Proposals', short: 'PR' },
    ],
  },
  {
    // Checking the management system works, rather than operating it.
    header: 'Oversight',
    items: [
      { to: '/meetings', label: 'Management review', short: 'MR' },
      { to: '/committees', label: 'Committees', short: 'CM' },
      { to: '/review', label: 'Discoveries', short: 'DI' },
    ],
  },
  {
    // Configuration, touched monthly at most. People & roles and Roles & access remain two pages
    // and are adjacent so the split is at least visible; merging them is a page refactor rather
    // than a menu change.
    header: 'Settings',
    items: [
      { to: '/people', label: 'People & roles', short: 'PE' },
      { to: '/admin', label: 'Roles & access', short: 'AD', adminOnly: true },
      { to: '/registers', label: 'Reference data', short: 'RF' },
    ],
  },
]

/**
 * Groups with nothing visible in them are dropped entirely.
 *
 * Otherwise a non-administrator would see a "Settings" heading over a shorter list, and an empty
 * heading reads as something failing to load rather than as something they may not use.
 */
const visibleNav = computed(() =>
  nav
    .map((g) => ({
      ...g,
      items: g.items.filter((n) => (!n.adminOnly || me.value?.roles?.includes('qms_admin')) && (!n.needsAny || n.needsAny.some((r) => me.value?.roles?.includes(r)))),
    }))
    .filter((g) => g.items.length > 0),
)

/**
 * The second menu: the same pages, grouped by the standard they serve.
 *
 * An audit is conducted against one standard at a time, and the task grouping above is the wrong
 * shape for walking somebody through it. Pages appear under several standards, which is correct
 * rather than a flaw — the document register genuinely evidences 27001's 7.5, 9001's 7.5.2 and
 * GDPR Article 28(3), and showing it once would misdescribe the other two.
 *
 * The mapping is in lib/standards.ts and nothing else reads it. Routes, guards and page content
 * are untouched: this is a view over the same navigation, not a second application. A page absent
 * from the mapping simply does not appear here, which is the safe direction, because the task
 * grouping still reaches everything.
 */
const menuMode = ref<'task' | 'standard'>(localStorage.getItem('qms.menu') === 'standard' ? 'standard' : 'task')
const standardKey = ref<string>(localStorage.getItem('qms.standard') || defaultStandardKey)

function setMenuMode(m: 'task' | 'standard') {
  menuMode.value = m
  localStorage.setItem('qms.menu', m)
}
function setStandard(k: string) {
  standardKey.value = k
  localStorage.setItem('qms.standard', k)
}

const currentStandard = computed(() => standardByKey(standardKey.value))

/**
 * Same administrator filter as the task menu, and the same rule about empty groups. An entry with
 * no route is kept: those are the things that do not exist yet, and dropping them would turn this
 * menu into a claim that the standard is covered.
 */
const standardNav = computed(() =>
  currentStandard.value.sections
    .map((sec) => ({
      ...sec,
      items: sec.items.filter((i) => !i.adminOnly || me.value?.roles?.includes('qms_admin')),
    }))
    .filter((sec) => sec.items.length > 0),
)

/** Tooltip text for one entry, used both collapsed and as the title on a missing row. */
function entryTitle(e: StandardEntry): string {
  return e.missing ? `${e.label} — ${e.ref}: ${e.missing}` : `${e.label} — ${e.ref}: ${e.note}`
}

onMounted(async () => {
  const stored = localStorage.getItem('qms.mode')
  dark.value = stored ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches
  applyTheme()

  if (!authConfigured && !devBypass) {
    // Deployed without a tenant/client id. Say so plainly rather than looping on a
    // sign-in that cannot succeed.
    authError.value = 'Authentication is not configured for this deployment.'
    loading.value = false
    return
  }
  try {
    me.value = await api.me()
  } catch (err) {
    if (err instanceof ApiError && err.isAuth) {
      const code = (err.body as { code?: string } | undefined)?.code
      if (code) {
        // Signed in, but the token is not one this API can accept. That is a
        // configuration problem with a named fix, not a prompt to sign in again —
        // showing the sign-in button here would loop forever.
        tokenProblem.value = { code, hint: err.message }
      }
      me.value = null
    } else {
      authError.value = err instanceof Error ? err.message : String(err)
    }
  } finally {
    loading.value = false
  }
})

/**
 * Whether to draw the app shell — the sidebar and the page title.
 *
 * False until the API has resolved a caller, not merely until MSAL reports a
 * session: `me` comes from /api/v1/me, so this follows the same gate the data
 * follows rather than a second, client-side opinion of who is signed in.
 *
 * This matters more than it looks. The sidebar names every register, and in
 * standard mode it names the ISO controls we claim and the gaps where we have no
 * coverage yet. On an internal-only deployment that was navigation; on
 * qms.abax.services it is a description of the compliance programme, readable by
 * anyone who loads the page. The API would still refuse every request behind those
 * labels, but the labels are the disclosure.
 */
const shellVisible = computed(() => (signedIn() || devBypass) && me.value !== null)

/**
 * Page titles name registers too — "Asset register", "Supplier register" — so a
 * signed-out visitor on a deep link gets the generic name instead.
 */
const headerTitle = computed(() =>
  shellVisible.value ? (route.meta.title as string | undefined) ?? 'ABAX Asset and Risk' : 'ABAX Asset and Risk',
)

const initials = computed(() =>
  (me.value?.name ?? me.value?.upn ?? '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join(''),
)
</script>

<template>
  <!-- Task screens render without the app shell.
       Performing a check is a task, not a page: it is done one-handed while walking a building,
       and a sidebar plus a header spend a third of a phone screen on navigation nobody needs
       mid-corridor. The screen keeps its own way back, which is the only navigation it does need. -->
  <div v-if="route.meta.fullscreen && (signedIn() || devBypass)" class="min-h-dvh">
    <RouterView />
  </div>

  <div v-else class="flex min-h-dvh">
    <!-- Sidebar. Collapsible to a strip of labels rather than hidden: the registers are wide
         and the width is worth reclaiming, but navigation you cannot see is navigation you
         cannot use. -->
    <aside
      v-if="shellVisible"
      class="hidden shrink-0 flex-col border-r border-[var(--ax-color-border)] bg-[var(--ax-color-card)] transition-[width] duration-150 md:flex"
      :class="collapsed ? 'w-14' : 'w-56'"
    >
      <div
        class="flex h-14 items-center gap-2 border-b border-[var(--ax-color-border)]"
        :class="collapsed ? 'justify-center px-0' : 'px-4'"
      >
        <template v-if="!collapsed">
          <span class="text-sm font-semibold tracking-tight">ABAX</span>
          <span class="text-sm text-[var(--ax-color-muted-foreground)]">Asset&nbsp;&amp;&nbsp;Risk</span>
        </template>
        <span v-else class="text-sm font-semibold tracking-tight">AR</span>
      </div>

      <nav class="flex-1 overflow-y-auto p-2" aria-label="Main">
        <!-- The mode switch. Expanded it is two labelled buttons, because a person who has never
             seen it has to be able to work out what it does; collapsed it is one button, because
             56 pixels will not hold two. -->
        <div v-if="!collapsed" class="mb-3 flex rounded border border-[var(--ax-color-border)] p-0.5" role="group" aria-label="Group the menu by">
          <button
            v-for="m in (['task', 'standard'] as const)"
            :key="m"
            type="button"
            class="flex-1 rounded px-2 py-1 text-[11px] transition-colors"
            :class="
              menuMode === m
                ? 'bg-[var(--ax-color-primary)]/10 font-medium text-[var(--ax-color-primary)]'
                : 'text-[var(--ax-color-muted-foreground)] hover:bg-[var(--ax-color-accent)]'
            "
            :aria-pressed="menuMode === m"
            @click="setMenuMode(m)"
          >
            {{ m === 'task' ? 'By task' : 'By standard' }}
          </button>
        </div>
        <button
          v-else
          type="button"
          class="mb-2 w-full rounded py-1 text-[11px] text-[var(--ax-color-muted-foreground)] hover:bg-[var(--ax-color-accent)]"
          :title="
            menuMode === 'task'
              ? 'Group the menu by standard'
              : `Grouped by ${currentStandard.label}. Switch back to grouping by task.`
          "
          @click="setMenuMode(menuMode === 'task' ? 'standard' : 'task')"
        >
          {{ menuMode === 'task' ? 'task' : 'std' }}
        </button>

        <!-- Which standard. Hidden when collapsed: the last choice is remembered, and a select
             box in a 56-pixel strip would show none of its options. -->
        <label v-if="menuMode === 'standard' && !collapsed" class="mb-2 block">
          <span class="sr-only">Standard</span>
          <select
            class="w-full rounded border border-[var(--ax-color-border)] bg-[var(--ax-color-card)] px-2 py-1 text-xs"
            :value="standardKey"
            @change="setStandard(($event.target as HTMLSelectElement).value)"
          >
            <option v-for="st in standards" :key="st.key" :value="st.key">{{ st.label }}</option>
          </select>
        </label>

        <!-- Coverage that is thin enough to mislead is said out loud, above the tree. A menu
             organised by standard reads as a claim to cover it, and for ISO 14001 that claim
             would be false. -->
        <p
          v-if="menuMode === 'standard' && !collapsed && currentStandard.caveat"
          class="mb-3 rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-[11px] leading-snug text-[var(--ax-color-muted-foreground)]"
        >
          {{ currentStandard.caveat }}
        </p>

        <!-- One list per group, with the heading rendered as a real <h2> inside a <section> so the
             grouping exists for a screen reader too and not only visually. Collapsed, the headings
             become a rule: two-letter codes under a label nobody can read would be worse than the
             separation alone. -->
        <template v-if="menuMode === 'task'">
          <section
            v-for="(group, gi) in visibleNav"
            :key="group.header"
            :class="gi > 0 ? 'mt-4' : ''"
          >
            <h2
              v-if="!collapsed"
              class="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--ax-color-muted-foreground)]"
            >
              {{ group.header }}
            </h2>
            <div
              v-else
              class="mx-2 mb-1.5 border-t border-[var(--ax-color-border)]"
              :aria-label="group.header"
            ></div>
            <RouterLink
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              class="block rounded py-2 text-sm transition-colors"
              :class="[
                route.path.startsWith(item.to)
                  ? 'bg-[var(--ax-color-primary)]/10 font-medium text-[var(--ax-color-primary)]'
                  : 'text-[var(--ax-color-foreground)] hover:bg-[var(--ax-color-accent)]',
                collapsed ? 'px-0 text-center' : 'px-3',
              ]"
              :title="collapsed ? `${group.header}: ${item.label}` : undefined"
            >
              <span v-if="collapsed" class="mono text-xs">{{ item.short }}</span>
              <span v-else>{{ item.label }}</span>
            </RouterLink>
          </section>
        </template>

        <!-- Grouped by standard. Each entry carries its clause, because the clause is what
             actually tells a reader why the page is in front of them; the folder does not.
             Entries with no route are the things ABAX does not hold yet and they say so. -->
        <template v-else>
          <section
            v-for="(sec, si) in standardNav"
            :key="sec.header"
            :class="si > 0 ? 'mt-4' : ''"
          >
            <h2
              v-if="!collapsed"
              class="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--ax-color-muted-foreground)]"
            >
              {{ sec.header }}
            </h2>
            <div
              v-else
              class="mx-2 mb-1.5 border-t border-[var(--ax-color-border)]"
              :aria-label="sec.header"
            ></div>

            <template v-for="item in sec.items" :key="item.label">
              <RouterLink
                v-if="item.to"
                :to="item.to"
                class="block rounded py-1.5 transition-colors"
                :class="[
                  route.path.startsWith(item.to)
                    ? 'bg-[var(--ax-color-primary)]/10 text-[var(--ax-color-primary)]'
                    : 'text-[var(--ax-color-foreground)] hover:bg-[var(--ax-color-accent)]',
                  collapsed ? 'px-0 text-center' : 'px-3',
                ]"
                :title="collapsed ? `${currentStandard.label} ${item.ref} — ${item.label}` : item.note"
              >
                <span v-if="collapsed" class="mono text-xs">{{ item.short }}</span>
                <template v-else>
                  <span class="block text-sm leading-5">{{ item.label }}</span>
                  <span class="mono block text-[10px] leading-4 text-[var(--ax-color-muted-foreground)]">{{ item.ref }}</span>
                </template>
              </RouterLink>

              <!-- Nothing to click. Listed anyway, with the reason: leaving it out would let the
                   tree imply the standard is covered when it is not. -->
              <div
                v-else
                class="rounded py-1.5"
                :class="collapsed ? 'px-0 text-center' : 'px-3'"
                :title="entryTitle(item)"
              >
                <span
                  v-if="collapsed"
                  class="mono text-xs text-[var(--ax-color-muted-foreground)] line-through decoration-1"
                  >{{ item.short }}</span
                >
                <template v-else>
                  <span class="block text-sm leading-5 text-[var(--ax-color-muted-foreground)]">{{ item.label }}</span>
                  <span class="mono block text-[10px] leading-4 text-[var(--ax-color-muted-foreground)]">
                    {{ item.ref }} · {{ item.missing }}
                  </span>
                </template>
              </div>
            </template>
          </section>
        </template>
      </nav>

      <div class="border-t border-[var(--ax-color-border)] p-2">
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-[var(--ax-color-muted-foreground)] hover:bg-[var(--ax-color-accent)]"
          :class="collapsed ? 'justify-center' : ''"
          :title="collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'"
          :aria-expanded="!collapsed"
          @click="toggleSidebar"
        >
          <span aria-hidden="true">{{ collapsed ? '»' : '«' }}</span>
          <span v-if="!collapsed">Collapse</span>
        </button>
        <p v-if="!collapsed" class="px-2 pt-2 text-xs text-[var(--ax-color-muted-foreground)]">
          ISO 27001 · 9001 · 14001
        </p>
      </div>
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <header
        class="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-[var(--ax-color-border)] bg-[var(--ax-color-card)] px-4"
      >
        <h1 class="truncate text-sm font-semibold">{{ headerTitle }}</h1>

        <div class="flex items-center gap-3">
          <!-- Only once signed in. The feed is per-person by construction and there is nothing
               to derive for a caller the API has not resolved. -->
          <NotificationBell v-if="me" />

          <button
            type="button"
            class="rounded border border-[var(--ax-color-border)] px-2 py-1 text-xs hover:bg-[var(--ax-color-accent)]"
            :aria-pressed="dark"
            @click="toggleTheme"
          >
            {{ dark ? 'Light' : 'Dark' }}
          </button>

          <template v-if="me">
            <div class="hidden text-right sm:block">
              <div class="text-xs font-medium leading-4">{{ me.name || me.upn }}</div>
              <div class="text-xs leading-4 text-[var(--ax-color-muted-foreground)]">
                {{ arr(me.roles).filter((r) => r !== 'employee').join(', ') || 'employee' }}
              </div>
            </div>
            <span
              class="grid size-8 place-items-center rounded-full bg-[var(--ax-color-primary)] text-xs font-semibold text-[var(--ax-color-primary-foreground)]"
              :title="me.upn"
            >
              {{ initials }}
            </span>
            <button
              type="button"
              class="text-xs text-[var(--ax-color-muted-foreground)] hover:underline"
              @click="signOut()"
            >
              Sign out
            </button>
          </template>
        </div>
      </header>

      <main class="min-w-0 flex-1 p-4 md:p-6">
        <div v-if="loading" class="flex items-center gap-2 text-sm text-[var(--ax-color-muted-foreground)]">
          <Spinner /> Loading…
        </div>

        <!-- Deployment-level auth problem. Distinct from "not signed in": there is
             nothing the user can do about it, so do not offer them a button. -->
        <div
          v-else-if="authError"
          class="rounded-lg border border-[var(--ax-color-destructive)]/30 bg-[var(--ax-color-destructive)]/5 p-4"
        >
          <p class="text-sm font-medium text-[var(--ax-color-destructive)]">Cannot sign in</p>
          <p class="mt-1 text-sm text-[var(--ax-color-muted-foreground)]">{{ authError }}</p>
        </div>

        <!-- A token the API will not accept. Distinct from being signed out: there is
             nothing to click, only something to fix in the app registration. -->
        <div
          v-else-if="tokenProblem"
          class="mx-auto max-w-xl rounded-lg border border-amber-500/40 bg-amber-500/5 p-4"
        >
          <p class="font-medium">Signed in, but the API rejected the token</p>
          <p class="mt-2 text-sm text-[var(--ax-color-muted-foreground)]">{{ tokenProblem.hint }}</p>
          <p class="mt-3 text-xs text-[var(--ax-color-muted-foreground)]">
            Reason code: <span class="mono">{{ tokenProblem.code }}</span>
          </p>
          <button
            type="button"
            class="mt-4 rounded border border-[var(--ax-color-border)] px-3 py-1.5 text-sm hover:bg-[var(--ax-color-accent)]"
            @click="signOut()"
          >
            Sign out and retry
          </button>
        </div>

        <div v-else-if="(!signedIn() && !devBypass) || !me" class="mx-auto max-w-md pt-16 text-center">
          <h2 class="text-lg font-semibold">ABAX Asset and Risk</h2>
          <p class="mt-2 text-sm text-[var(--ax-color-muted-foreground)]">
            The asset and risk register for ISO 27001, 9001 and 14001. Sign in with your
            ABAX account.
          </p>
          <button
            type="button"
            class="mt-6 rounded bg-[var(--ax-color-primary)] px-4 py-2 text-sm font-medium text-[var(--ax-color-primary-foreground)] hover:opacity-90"
            @click="signIn()"
          >
            Sign in
          </button>
        </div>

        <template v-else>
          <div
            v-if="!me.enabled"
            class="mb-4 rounded-lg border border-[var(--ax-color-destructive)]/30 bg-[var(--ax-color-destructive)]/5 p-3 text-sm"
          >
            Your account is disabled. <Badge tone="danger">no access</Badge>
          </div>
          <RouterView />
        </template>
      </main>
    </div>
  </div>
</template>
